<template>
  <section v-if="state !== 'hidden'" class="ops-push" :class="`ops-push--${state}`" :data-state="state" data-test="ops-push-card">
    <span class="ops-push__icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
    </span>

    <div class="ops-push__main">
      <div class="ops-push__title" data-test="ops-push-title">
        {{ title }}
        <span v-if="state === 'enabled'" class="ops-push__on" data-test="ops-push-on">On</span>
      </div>

      <p v-if="state === 'ios-needs-home-screen'" class="ops-push__text" data-test="ops-push-text">
        On iPhone, alerts need Teamio on your Home Screen (iOS 16.4 or newer).
      </p>
      <ol v-if="state === 'ios-needs-home-screen'" class="ops-push__steps" data-test="ops-push-steps">
        <li>Tap the <strong>Share</strong> button in Safari.</li>
        <li>Choose <strong>Add to Home Screen</strong>.</li>
        <li>Open Teamio from its new icon, come back to Alerts and tap <strong>Enable alerts on this phone</strong>.</li>
      </ol>

      <p v-else-if="state === 'can-enable'" class="ops-push__text" data-test="ops-push-text">
        Get a notification on this phone when someone pings you, even when Teamio is closed.
      </p>

      <p v-else-if="state === 'enabled'" class="ops-push__text" data-test="ops-push-text">
        This phone will notify you when someone pings you.
      </p>

      <p v-else-if="state === 'denied'" class="ops-push__text" data-test="ops-push-text">
        Notifications are blocked for Teamio on this phone. Turn them on in the phone's Settings (Notifications, then Teamio or your browser), then come back here.
      </p>

      <p v-if="message" class="ops-push__msg" role="alert" data-test="ops-push-msg">{{ message }}</p>

      <div v-if="state === 'can-enable' || state === 'enabled'" class="ops-push__actions">
        <button
          v-if="state === 'can-enable'"
          type="button"
          class="ops-push__btn ops-push__btn--primary"
          :disabled="busy"
          data-test="ops-push-enable"
          @click="onEnable"
        >{{ busy ? 'Turning on…' : 'Enable alerts on this phone' }}</button>
        <button
          v-else
          type="button"
          class="ops-push__btn"
          :disabled="busy"
          data-test="ops-push-disable"
          @click="onDisable"
        >{{ busy ? 'Turning off…' : 'Turn off' }}</button>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * "Enable alerts on this phone" (Practice Ops web push, plan §5): one card, five states, all decided by pushService.getState()
 * (the pure rule is utils/practiceOps/pushSupport.js pushState):
 *
 *   hidden                 nothing is drawn — no VAPID key in this build, or the browser cannot do push
 *   ios-needs-home-screen  Add-to-Home-Screen steps (iOS only delivers web push to an installed web app)
 *   can-enable             the button; the permission prompt is raised by THAT TAP, never on load
 *   enabled                "On" + Turn off
 *   denied                 how to allow notifications again in the phone's settings
 *
 * It is drawn by the Phone view's Alerts tab. Props are `employeeId` / `siteKey` (the signed-in employee and the office); no prop
 * is named siteID / patNum / loading (mixin.js computeds shadow those).
 */
import * as pushService from '@/services/pushService';

const MESSAGES = {
  denied: 'Notifications are blocked. Turn them on in the phone\'s Settings, then try again.',
  dismissed: 'Alerts were not turned on. Tap the button to try again.',
  unavailable: 'This browser cannot receive alerts. Try Safari or Chrome.',
  error: 'Could not turn alerts on. Check your connection and try again.',
};

export default {
  name: 'OpsEnableAlertsCard',

  props: {
    /** The signed-in employee (the person pings are addressed to). */
    employeeId: { type: String, default: '' },
    /** The office (site) the employee works at. */
    siteKey: { type: String, default: '' },
  },

  data() {
    return { state: 'hidden', busy: false, message: '' };
  },

  computed: {
    title() {
      if (this.state === 'enabled') return 'Alerts';
      if (this.state === 'denied') return 'Alerts are blocked';
      if (this.state === 'ios-needs-home-screen') return 'Get alerts on this phone';
      return 'Alerts on this phone';
    },
  },

  watch: {
    employeeId() { this.load(); },
  },

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      // A page can be mounted for a while before the signed-in employee is known; no employee ⇒ nothing to decide yet.
      this.state = this.employeeId ? await pushService.getState(this.employeeId) : 'hidden';
    },

    async onEnable() {
      if (this.busy) return;
      this.busy = true;
      this.message = '';
      const result = await pushService.enable(this.employeeId, this.siteKey);
      this.busy = false;
      if (!result.ok) this.message = MESSAGES[result.reason] || MESSAGES.error;
      await this.load();
    },

    async onDisable() {
      if (this.busy) return;
      this.busy = true;
      this.message = '';
      await pushService.disable(this.employeeId, this.siteKey);
      this.busy = false;
      await this.load();
    },
  },
};
</script>

<style lang="scss" scoped>
// The Phone view's own tokens: teal #0f7259 / #0b5c49 on #e9f6f1 / #b9dccd, muted #707876, ink #0c0f0e (see OpsPhone.vue).
.ops-push {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid #b9dccd;
  border-radius: 12px;
  background: #e9f6f1;
  color: #0c0f0e;
}

.ops-push--denied {
  border-color: #ead3a1;
  background: #fdf6e7;
}

.ops-push__icon {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #fff;
  color: #0f7259;

  svg { display: block; }
}

.ops-push--denied .ops-push__icon { color: #b45309; }

.ops-push__main {
  flex: 1 1 auto;
  min-width: 0;
}

.ops-push__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 800;
}

.ops-push__on {
  padding: 1px 8px;
  border-radius: 999px;
  background: #0f7259;
  color: #fff;
  font-size: 11.5px;
  font-weight: 700;
}

.ops-push__text,
.ops-push__msg {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #3d4745;
}

.ops-push__msg {
  color: #b45309;
  font-weight: 600;
}

.ops-push__steps {
  margin: 6px 0 0;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.5;
  color: #3d4745;
}

.ops-push__actions {
  margin-top: 10px;
}

.ops-push__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 16px;
  border: 1px solid #b9dccd;
  border-radius: 10px;
  background: #fff;
  color: #0b5c49;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: default; }
  &:focus-visible { outline: 2px solid #0f7259; outline-offset: 2px; }

  &--primary {
    border-color: #0f7259;
    background: #0f7259;
    color: #fff;
  }
}
</style>
